import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Bell,
  X,
  CheckCheck,
  Sparkles,
  CheckCircle2,
  Clock,
  BookOpen,
  Volume2,
  VolumeX,
  ExternalLink,
  Trash2,
  Building2,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { useNotificationStore } from '../store/useNotificationStore.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface NotificationDrawerProps {
  onSelectBook?: (bookId: string) => void;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ onSelectBook }) => {
  const { token } = useAuthStore();
  const {
    notifications,
    unreadCount,
    isOpen,
    toggleOpen,
    pushEnabled,
    setPushEnabled,
    fetchNotifications,
    markAsRead,
    markAllRead,
    triggerTestPush,
    deleteNotification,
    deleteReadNotifications,
  } = useNotificationStore();

  useEffect(() => {
    if (token) {
      fetchNotifications(token);
      const interval = setInterval(() => {
        fetchNotifications(token);
      }, 12000); // 12s polling for instant push notifications
      return () => clearInterval(interval);
    }
  }, [token, fetchNotifications]);

  useBodyScrollLock(isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 overflow-hidden bg-slate-900/50 backdrop-blur-xs flex justify-end"
          onClick={(e) => {
            if (e.target === e.currentTarget) toggleOpen(false);
          }}
        >
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="w-full sm:max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col h-full"
          >
            {/* Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/90">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center relative shrink-0">
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white font-bold text-[9px] rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base truncate">Pusat Notifikasi Push</h3>
                  <p className="text-[11px] text-slate-500 truncate">Pembaruan otomatis status buku & UIBN</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => toggleOpen(false)}
                className="w-10 h-10 min-w-[44px] min-h-[44px] rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 flex items-center justify-center transition cursor-pointer shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick controls bar */}
            <div className="p-3 bg-slate-50/60 border-b border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs px-3.5 sm:px-4">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => setPushEnabled(!pushEnabled)}
                  className={`px-3 py-2 min-h-[44px] rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                    pushEnabled
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {pushEnabled ? <Volume2 className="w-3.5 h-3.5 text-indigo-600" /> : <VolumeX className="w-3.5 h-3.5" />}
                  <span>{pushEnabled ? 'Push Suara' : 'Hening'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => triggerTestPush(token)}
                  className="px-3 py-2 min-h-[44px] rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 text-xs font-medium flex items-center justify-center gap-1 transition cursor-pointer"
                  title="Kirim notifikasi pengujian status"
                >
                  <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                  <span>Tes Push</span>
                </button>
              </div>

              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllRead(token)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold flex items-center justify-center gap-1 cursor-pointer min-h-[44px] px-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    <span>Baca Semua</span>
                  </button>
                )}

                {notifications.some((n) => n.isRead === 1) && (
                  <button
                    type="button"
                    onClick={() => deleteReadNotifications(token)}
                    className="text-xs text-rose-600 hover:text-rose-800 font-semibold flex items-center justify-center gap-1 cursor-pointer min-h-[44px] px-1"
                    title="Hapus semua notifikasi yang sudah dibaca"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Hapus Dibaca</span>
                  </button>
                )}
              </div>
            </div>

            {/* Notification Items List */}
            <div className="flex-1 overflow-y-auto p-3.5 sm:p-4">
              {notifications.length === 0 ? (
                <div className="py-16 text-center text-slate-400">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Belum ada notifikasi.</p>
                  <p className="text-[11px] text-slate-400 mt-1 max-w-xs mx-auto">
                    Anda akan menerima notifikasi otomatis saat naskah buku diverifikasi atau diubah statusnya.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence initial={false}>
                    {notifications.map((n, nIdx) => (
                      <motion.div
                        key={`${n.id}-${nIdx}`}
                        layout
                        initial={{ opacity: 0, height: 0, scale: 0.95 }}
                        animate={{ opacity: 1, height: 'auto', scale: 1 }}
                        exit={{
                          opacity: 0,
                          height: 0,
                          x: -200,
                          scale: 0.95,
                          transition: { duration: 0.25, ease: 'easeInOut' }
                        }}
                        className="relative overflow-hidden rounded-xl bg-red-500/10 border border-transparent"
                      >
                        {/* Red Delete Background Revealed on Swipe Left */}
                        <div className="absolute inset-y-0 right-0 w-32 bg-red-600 flex items-center justify-end pr-5 text-white gap-1.5 rounded-r-xl select-none">
                          <Trash2 className="w-4 h-4 animate-pulse" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Hapus</span>
                        </div>

                        {/* Draggable Top Card Layer */}
                        <motion.div
                          drag="x"
                          dragDirectionLock
                          dragConstraints={{ left: -100, right: 0 }}
                          dragElastic={{ left: 0.1, right: 0.15 }}
                          onDragEnd={(_, info) => {
                            // If user swiped left past the threshold (e.g., -60px)
                            if (info.offset.x < -60) {
                              deleteNotification(n.id, token);
                            }
                          }}
                          whileTap={{ cursor: 'grabbing' }}
                          onClick={() => {
                            if (n.isRead === 0) markAsRead(n.id, token);
                          }}
                          className={`relative z-10 p-3.5 rounded-xl border flex gap-3 items-start select-none cursor-grab active:cursor-grabbing transition-colors duration-150 ${
                            n.isRead === 0
                              ? 'bg-indigo-50/90 border-indigo-200/80 shadow-2xs'
                              : 'bg-white border-slate-100 hover:bg-slate-50'
                          }`}
                        >
                          {/* Icon by notification type */}
                          <div
                            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 pointer-events-none ${
                              n.type === 'publisher_verification_request'
                                ? 'bg-amber-100 text-amber-700'
                                : n.type === 'verification_approved'
                                ? 'bg-emerald-100 text-emerald-700'
                                : n.type === 'verification_rejected'
                                ? 'bg-rose-100 text-rose-700'
                                : n.type === 'verification_pending'
                                ? 'bg-amber-100 text-amber-700'
                                : n.type === 'status_change'
                                ? 'bg-indigo-100 text-indigo-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {n.type === 'publisher_verification_request' ? (
                              <Building2 className="w-4 h-4 text-amber-600" />
                            ) : n.type === 'verification_approved' ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : n.type === 'verification_rejected' ? (
                              <ShieldAlert className="w-4 h-4 text-rose-600" />
                            ) : n.type === 'verification_pending' ? (
                              <Clock className="w-4 h-4 text-amber-600" />
                            ) : n.type === 'status_change' ? (
                              <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                            ) : (
                              <Bell className="w-4 h-4 text-indigo-600" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0 pointer-events-none">
                            <div className="flex items-center justify-between gap-2">
                              <h4 className="text-xs font-bold text-slate-900 truncate flex-1 pr-1">
                                {n.title}
                              </h4>
                              <div className="flex items-center gap-1.5 shrink-0">
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {new Date(n.createdAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNotification(n.id, token);
                                  }}
                                  className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-slate-100 transition pointer-events-auto cursor-pointer"
                                  title="Hapus Notifikasi"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                              {n.message}
                            </p>

                            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                              <span>{new Date(n.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}</span>
                              {n.isRead === 0 && (
                                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wide">
                                  Baru
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
